import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '@/lib/currency';
import {
  Plus,
  Minus,
  Trash2,
  Bed,
  BedDouble,
  BedSingle,
  Sofa,
  Baby,
  ChevronDown,
  ChevronUp,
  GripVertical,
} from 'lucide-react';

// Bed types with icons. Width ranges live in BED_WIDTHS_CM below (one source of
// truth, shared with the guest portal) and are shown as a "120–150 cm" caption.
const BED_TYPES = [
  { id: 'double', icon: BedDouble, sleeps: 2 },
  { id: 'queen', icon: BedDouble, sleeps: 2 },
  { id: 'king', icon: BedDouble, sleeps: 2 },
  { id: 'single', icon: BedSingle, sleeps: 1 },
  { id: 'twin', icon: BedSingle, sleeps: 2 }, // 2 single beds
  { id: 'sofa_bed', icon: Sofa, sleeps: 2 },
  { id: 'bunk', icon: Bed, sleeps: 2 },
  { id: 'crib', icon: Baby, sleeps: 1 },
  { id: 'floor_mattress', icon: Bed, sleeps: 1 },
  { id: 'air_mattress', icon: Bed, sleeps: 1 },
] as const;

/**
 * Standard bed WIDTH ranges (cm) per type, following Booking.com conventions.
 * Exported so the guest portal (SleepingSection) shows the same numbers. Covers
 * every bed-type id used across the app, including guest-only variants the
 * picker doesn't offer (super_king, sofa_bed_single/double, futon…). null/absent
 * = no meaningful single width.
 */
export const BED_WIDTHS_CM: Record<string, readonly [number, number] | null> = {
  single: [90, 130],
  twin: [90, 130],
  double: [131, 150],
  queen: [151, 180],
  king: [181, 210],
  super_king: [200, 220],
  sofa_bed: null,
  sofa_bed_single: [80, 120],
  sofa_bed_double: [120, 150],
  bunk: [90, 130],
  bunk_bed: [90, 130],
  crib: [60, 70],
  toddler_bed: [70, 90],
  floor_mattress: null,
  air_mattress: null,
  extra_mattress: null,
  futon: [90, 140],
  daybed: [90, 120],
  murphy_bed: [140, 160],
  rollaway: [80, 100],
  armchair_bed: [70, 90],
};

/** Human "120–150 cm" label for a bed type's width, or '' when not applicable. */
export function bedWidthLabel(bedTypeId: string): string {
  const w = BED_WIDTHS_CM[bedTypeId];
  return w ? `${w[0]}–${w[1]} cm` : '';
}

type ExtraBeds = NonNullable<SleepingConfig['extraBeds']>;

/**
 * Per-type extra-bed counts, normalised across old and new data shapes.
 * - New data: `typeCounts` is authoritative.
 * - Old data: `types[]` + total `count` → spread evenly (at least 1 each).
 */
export function extraBedTypeCounts(extra: ExtraBeds | undefined): Record<string, number> {
  if (!extra) return {};
  if (extra.typeCounts && Object.keys(extra.typeCounts).length > 0) {
    return Object.fromEntries(Object.entries(extra.typeCounts).filter(([, n]) => n > 0));
  }
  const types = extra.types ?? [];
  if (types.length === 0) return {};
  // Old shape had a single total; default each listed type to 1.
  return Object.fromEntries(types.map((tp) => [tp, 1]));
}

/** Total extra beds across all types (new shape sums typeCounts). */
export function extraBedsTotal(extra: ExtraBeds | undefined): number {
  if (!extra) return 0;
  const counts = extraBedTypeCounts(extra);
  const sum = Object.values(counts).reduce((a, b) => a + b, 0);
  // Fall back to the legacy total only when there are no per-type counts.
  return sum > 0 ? sum : (extra.count ?? 0);
}

/**
 * How many people each EXTRA-bed type sleeps. Extra beds aren't in BED_TYPES, so
 * they need their own map. Used to show "standard N + up to M on request" and,
 * later, on BookiScout where the offered capacity matters.
 */
export const EXTRA_BED_SLEEPS: Record<string, number> = {
  sofa_bed: 2,
  armchair_bed: 1,
  rollaway: 1,
  air_mattress: 1,
  floor_mattress: 1,
  crib: 1,
};

/** Total EXTRA guests these on-request beds can sleep (Σ count × sleeps). */
export function extraBedsCapacity(extra: ExtraBeds | undefined): number {
  const counts = extraBedTypeCounts(extra);
  return Object.entries(counts).reduce((sum, [type, n]) => sum + n * (EXTRA_BED_SLEEPS[type] ?? 1), 0);
}

// Room types
const ROOM_TYPES = [
  'bedroom',
  'living_room',
  'studio',
  'loft',
  'mezzanine',
] as const;

export interface BedConfig {
  type: string;
  count: number;
}

export interface RoomConfig {
  id: string;
  type: string;
  name?: string;
  beds: BedConfig[];
  /**
   * Optional manual override of how many people sleep in this room. When unset
   * (undefined), capacity is auto-derived from the beds (a double sleeps 2,
   * etc.). Set it when the bed count doesn't reflect reality — e.g. a double bed
   * used by a single guest.
   */
  capacity?: number;
}

export interface SleepingConfig {
  rooms: RoomConfig[];
  extraBeds?: {
    available: boolean;
    types?: string[];
    /**
     * How many of EACH extra-bed type are available, e.g. { sofa_bed: 1,
     * crib: 2 }. This is the precise source of truth. `count`/`types` are kept
     * for backward compatibility (old data, and code that only needs a total);
     * derive both from typeCounts when present (see extraBedsTotal / typesOf).
     */
    typeCounts?: Record<string, number>;
    /**
     * Per-type setup/usage note shown to the guest, e.g. how the sofa bed is
     * unfolded or where the crib is placed: { sofa_bed: "Rastavlja se…",
     * crib: "Postavlja se u sobi 1" }. `notes` below stays as a general note.
     */
    typeNotes?: Record<string, string>;
    count?: number;
    price?: number;
    notes?: string;
  };
  totalCapacity?: number;
}

interface BedConfiguratorProps {
  value: SleepingConfig;
  onChange: (config: SleepingConfig) => void;
  className?: string;
  currency?: string;
}

export function BedConfigurator({
  value,
  onChange,
  className = '',
  currency = 'EUR',
}: BedConfiguratorProps) {
  const { t } = useTranslation();
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());

  // Open the first room by default so the host immediately sees where to add
  // beds (e.g. after the wizard pre-fills rooms from the bedroom count). Runs
  // once on mount; manual expand/collapse afterwards is untouched.
  const didAutoExpand = useRef(false);
  useEffect(() => {
    if (!didAutoExpand.current && value.rooms.length > 0) {
      didAutoExpand.current = true;
      setExpandedRooms((prev) => (prev.size === 0 ? new Set([value.rooms[0].id]) : prev));
    }
  }, [value.rooms]);

  // Capacity a room sleeps: manual override when set, else derived from beds.
  const roomCapacity = (room: RoomConfig): number => {
    if (room.capacity !== undefined && room.capacity !== null) return room.capacity;
    return room.beds.reduce((roomTotal, bed) => {
      const bedType = BED_TYPES.find(b => b.id === bed.type);
      return roomTotal + (bedType?.sleeps || 1) * bed.count;
    }, 0);
  };

  // Calculate total capacity (sum of per-room capacities).
  const calculateCapacity = (rooms: RoomConfig[]): number => {
    return rooms.reduce((total, room) => total + roomCapacity(room), 0);
  };

  // Counter for generating unique IDs (stable across renders)
  const idCounterRef = useRef(0);

  // Add new room
  const addRoom = (type: string = 'bedroom') => {
    // Generate unique ID inside the handler (not during render)
    idCounterRef.current += 1;
    const newId = `room_${idCounterRef.current}_${Date.now()}`;
    const newRoom: RoomConfig = {
      id: newId,
      type,
      beds: [],
    };
    const newRooms = [...value.rooms, newRoom];
    onChange({
      ...value,
      rooms: newRooms,
      totalCapacity: calculateCapacity(newRooms),
    });
    // Auto-expand new room
    setExpandedRooms(prev => new Set([...prev, newRoom.id]));
  };

  // Remove room
  const removeRoom = (roomId: string) => {
    const newRooms = value.rooms.filter(r => r.id !== roomId);
    onChange({
      ...value,
      rooms: newRooms,
      totalCapacity: calculateCapacity(newRooms),
    });
  };

  // Update room
  const updateRoom = (roomId: string, updates: Partial<RoomConfig>) => {
    const newRooms = value.rooms.map(r =>
      r.id === roomId ? { ...r, ...updates } : r
    );
    onChange({
      ...value,
      rooms: newRooms,
      totalCapacity: calculateCapacity(newRooms),
    });
  };

  // Add bed to room
  const addBed = (roomId: string, bedType: string) => {
    const room = value.rooms.find(r => r.id === roomId);
    if (!room) return;

    const existingBed = room.beds.find(b => b.type === bedType);
    let newBeds: BedConfig[];

    if (existingBed) {
      newBeds = room.beds.map(b =>
        b.type === bedType ? { ...b, count: b.count + 1 } : b
      );
    } else {
      newBeds = [...room.beds, { type: bedType, count: 1 }];
    }

    updateRoom(roomId, { beds: newBeds });
  };

  // Remove bed from room
  const removeBed = (roomId: string, bedType: string) => {
    const room = value.rooms.find(r => r.id === roomId);
    if (!room) return;

    const newBeds = room.beds
      .map(b => b.type === bedType ? { ...b, count: b.count - 1 } : b)
      .filter(b => b.count > 0);

    updateRoom(roomId, { beds: newBeds });
  };

  // Toggle room expansion
  const toggleRoom = (roomId: string) => {
    setExpandedRooms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roomId)) {
        newSet.delete(roomId);
      } else {
        newSet.add(roomId);
      }
      return newSet;
    });
  };

  // Update extra beds
  const updateExtraBeds = (updates: Partial<SleepingConfig['extraBeds']>) => {
    onChange({
      ...value,
      extraBeds: {
        ...value.extraBeds,
        available: value.extraBeds?.available || false,
        ...updates,
      },
    });
  };

  // Get bed icon
  const getBedIcon = (bedType: string) => {
    const bed = BED_TYPES.find(b => b.id === bedType);
    return bed?.icon || Bed;
  };

  // Auto capacity from beds only (ignores manual override) — used to know
  // whether the host has deviated from the bed-derived number.
  const bedDerivedCapacity = (room: RoomConfig): number => {
    return room.beds.reduce((total, bed) => {
      const bedType = BED_TYPES.find(b => b.id === bed.type);
      return total + (bedType?.sleeps || 1) * bed.count;
    }, 0);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary — standard capacity from fixed beds, plus the on-request extra
          capacity shown separately so it reads as "6 + up to 2 = 8" (info only). */}
      {(() => {
        const standard = value.totalCapacity || calculateCapacity(value.rooms);
        const extra = value.extraBeds?.available ? extraBedsCapacity(value.extraBeds) : 0;
        return (
          <div className="flex items-center justify-between p-3 bg-[hsl(var(--accent))]/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Bed className="h-5 w-5 text-[hsl(var(--primary))]" />
              <div>
                <span className="font-medium">{value.rooms.length} </span>
                <span className="text-[hsl(var(--muted-foreground))]">
                  {t('unitDetails.sleeping.rooms', { count: value.rooms.length })}
                </span>
              </div>
            </div>
            <div className="text-right text-sm">
              <div>
                <span className="font-medium">{standard} </span>
                <span className="text-[hsl(var(--muted-foreground))]">
                  {extra > 0 ? t('unitDetails.sleeping.sleepsStandard', 'osoba (standard)') : t('unitDetails.sleeping.people', { count: standard })}
                </span>
              </div>
              {extra > 0 && (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {t('unitDetails.sleeping.capacityWithExtra', '+ do {{extra}} na zahtjev = ukupno {{total}}', { extra, total: standard + extra })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Rooms List */}
      <div className="space-y-2">
        {value.rooms.map((room, index) => {
          const isExpanded = expandedRooms.has(room.id);
          const Icon = room.type === 'living_room' ? Sofa : Bed;
          const capacity = roomCapacity(room);

          return (
            <div
              key={room.id}
              className="border border-[hsl(var(--border))] rounded-lg overflow-hidden"
            >
              {/* Room Header */}
              <button
                type="button"
                onClick={() => toggleRoom(room.id)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[hsl(var(--background))] hover:bg-[hsl(var(--accent))]/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <GripVertical className="h-4 w-4 text-[hsl(var(--muted-foreground))] cursor-grab" />
                  <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                  <div className="text-left">
                    <span className="font-medium">
                      {room.name || `${t(`unitDetails.sleeping.roomTypes.${room.type}`)} ${index + 1}`}
                    </span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))] ml-2">
                      ({capacity} {t('unitDetails.sleeping.people', { count: capacity })})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {room.beds.length > 0 && (
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {room.beds.map(b => `${b.count}× ${t(`unitDetails.sleeping.bedTypes.${b.type}`)}`).join(', ')}
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </button>

              {/* Room Content */}
              {isExpanded && (
                <div className="p-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--accent))]/10 space-y-4">
                  {/* Room Type & Name */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">
                        {t('unitDetails.sleeping.roomType')}
                      </label>
                      <select
                        value={room.type}
                        onChange={(e) => updateRoom(room.id, { type: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                      >
                        {ROOM_TYPES.map(type => (
                          <option key={type} value={type}>
                            {t(`unitDetails.sleeping.roomTypes.${type}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">
                        {t('unitDetails.sleeping.roomNameOptional', 'Naziv sobe (opcionalno)')}
                      </label>
                      <input
                        type="text"
                        value={room.name || ''}
                        onChange={(e) => updateRoom(room.id, { name: e.target.value })}
                        // Placeholder follows the picked room type so it never says
                        // "spavaća soba" for a Studio; the name is optional either way.
                        placeholder={t('unitDetails.sleeping.roomNamePlaceholderTyped', 'npr. {{type}}', { type: t(`unitDetails.sleeping.roomTypes.${room.type}`) })}
                        className="w-full px-3 py-2 rounded-lg border bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                      />
                    </div>
                  </div>

                  {/* Current Beds */}
                  {room.beds.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-xs text-[hsl(var(--muted-foreground))]">
                        {t('unitDetails.sleeping.currentBeds')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {room.beds.map(bed => {
                          const BedIcon = getBedIcon(bed.type);
                          return (
                            <div
                              key={bed.type}
                              className="flex items-center gap-2 px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg"
                            >
                              <BedIcon className="h-4 w-4 text-[hsl(var(--primary))]" />
                              <span className="text-sm">
                                {bed.count}× {t(`unitDetails.sleeping.bedTypes.${bed.type}`)}
                                {bedWidthLabel(bed.type) && (
                                  <span className="ml-1 text-xs text-[hsl(var(--muted-foreground))]">({bedWidthLabel(bed.type)})</span>
                                )}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeBed(room.id, bed.type)}
                                className="p-1 hover:bg-[hsl(var(--destructive))]/10 rounded text-[hsl(var(--destructive))]"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add Bed */}
                  <div className="space-y-2">
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">
                      {t('unitDetails.sleeping.addBed')}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                      {BED_TYPES.map(bedType => {
                        const BedIcon = bedType.icon;
                        const width = bedWidthLabel(bedType.id);
                        return (
                          <button
                            key={bedType.id}
                            type="button"
                            onClick={() => addBed(room.id, bedType.id)}
                            className="flex flex-col items-center gap-1 p-2 border border-[hsl(var(--border))] rounded-lg hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))]/30 transition-colors"
                          >
                            <BedIcon className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                            <span className="text-xs text-center leading-tight">
                              {t(`unitDetails.sleeping.bedTypes.${bedType.id}`)}
                            </span>
                            {width && (
                              <span className="text-[10px] text-center text-[hsl(var(--muted-foreground))]">{width}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sleeps in this room — auto from beds, manually adjustable.
                      A double bed defaults to 2, but the host can set 1 (or any
                      number) when reality differs. "Auto" clears the override. */}
                  <div className="space-y-1.5">
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">
                      {t('unitDetails.sleeping.sleepsInRoom', 'Broj osoba u sobi')}
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2 py-1.5">
                        <button
                          type="button"
                          aria-label="-"
                          onClick={() => updateRoom(room.id, { capacity: Math.max(0, capacity - 1) })}
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums">{capacity}</span>
                        <button
                          type="button"
                          aria-label="+"
                          onClick={() => updateRoom(room.id, { capacity: capacity + 1 })}
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {room.capacity !== undefined && room.capacity !== null ? (
                        <button
                          type="button"
                          onClick={() => updateRoom(room.id, { capacity: undefined })}
                          className="text-xs font-medium text-[hsl(var(--primary))] hover:underline"
                        >
                          {t('unitDetails.sleeping.capacityAuto', 'Auto ({{n}})', { n: bedDerivedCapacity(room) })}
                        </button>
                      ) : (
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {t('unitDetails.sleeping.capacityFromBeds', 'Automatski iz kreveta')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Remove Room */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeRoom(room.id)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('unitDetails.sleeping.removeRoom')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Room */}
      <div className="flex flex-wrap gap-2">
        {ROOM_TYPES.map(type => (
          <button
            key={type}
            type="button"
            onClick={() => addRoom(type)}
            className="flex items-center gap-2 px-3 py-2 border border-dashed border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t(`unitDetails.sleeping.addRoomType.${type}`)}
          </button>
        ))}
      </div>

      {/* Extra Beds Section */}
      <div className="border-t border-[hsl(var(--border))] pt-4 mt-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="extraBeds"
            checked={value.extraBeds?.available || false}
            onChange={(e) => updateExtraBeds({ available: e.target.checked })}
            className="h-4 w-4 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-[hsl(var(--ring))]"
          />
          <label htmlFor="extraBeds" className="font-medium">
            {t('unitDetails.sleeping.extraBeds.title')}
          </label>
        </div>
        {/* Always-visible one-liner so the host knows what this is BEFORE ticking. */}
        <p className="ml-7 mt-1 text-xs text-[hsl(var(--muted-foreground))]">
          {t('unitDetails.sleeping.extraBeds.hint', 'Ako u ovu jedinicu možeš donijeti dodatni krevet kad gost treba više mjesta, označi i upiši koliko kojeg.')}
        </p>

        {value.extraBeds?.available && (
          <div className="ml-7 mt-3 space-y-3">
            <p className="rounded-lg bg-[hsl(var(--muted))]/40 p-2 text-xs text-[hsl(var(--muted-foreground))]">
              {t('unitDetails.sleeping.extraBeds.note', 'ℹ️ Ovo su samo dodatni KREVETI. Ostalu opremu (sjedalica za bebe, suncobran…) dodaješ u sekciji „Dodatna oprema".')}
            </p>
            {/* Per-type counts: pick a type to add it, then set how many of EACH.
                This replaces the old "single total + flat type list" so it's clear
                whether 2 extra beds means 2 sofa beds or 1 sofa bed + 1 crib. */}
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">
                {t('unitDetails.sleeping.extraBeds.typesCount', 'Koliko kojeg dodatnog kreveta')}
              </label>
              <div className="space-y-2">
                {['sofa_bed', 'armchair_bed', 'rollaway', 'air_mattress', 'floor_mattress', 'crib'].map(type => {
                  const counts = extraBedTypeCounts(value.extraBeds);
                  const n = counts[type] ?? 0;
                  const setN = (next: number) => {
                    const cur = { ...counts };
                    if (next <= 0) delete cur[type];
                    else cur[type] = next;
                    updateExtraBeds({ typeCounts: cur, types: Object.keys(cur), count: extraBedsTotal({ available: true, typeCounts: cur }) });
                  };
                  const on = n > 0;
                  const width = bedWidthLabel(type);
                  const typeNote = value.extraBeds?.typeNotes?.[type] ?? '';
                  const setTypeNote = (note: string) => {
                    const cur = { ...(value.extraBeds?.typeNotes ?? {}) };
                    if (note.trim()) cur[type] = note; else delete cur[type];
                    updateExtraBeds({ typeNotes: cur });
                  };
                  return (
                    <div
                      key={type}
                      className={`rounded-lg border transition-colors ${
                        on ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-[hsl(var(--border))]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setN(on ? 0 : 1)}
                          className="flex-1 text-left text-sm font-medium"
                        >
                          {t(`unitDetails.sleeping.bedTypes.${type}`)}
                          {width && <span className="ml-1.5 text-xs text-[hsl(var(--muted-foreground))]">{width}</span>}
                          <span className="ml-1.5 text-xs text-[hsl(var(--muted-foreground))]">· {t('unitDetails.sleeping.extraBeds.sleeps', 'za {{n}} os.', { n: EXTRA_BED_SLEEPS[type] ?? 1 })}</span>
                        </button>
                        <div className="flex items-center gap-2">
                          <button type="button" aria-label="-" onClick={() => setN(n - 1)} disabled={n <= 0} className="flex h-7 w-7 items-center justify-center rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] disabled:opacity-40">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-[1.5ch] text-center text-sm font-semibold tabular-nums">{n}</span>
                          <button type="button" aria-label="+" onClick={() => setN(n + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Per-type setup/usage note (e.g. how to unfold, where it goes). */}
                      {on && (
                        <div className="px-3 pb-2.5">
                          <input
                            type="text"
                            value={typeNote}
                            onChange={(e) => setTypeNote(e.target.value)}
                            placeholder={t('unitDetails.sleeping.extraBeds.typeNotePlaceholder', 'Upute (npr. rastavlja se, gdje se postavlja…)')}
                            className="w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">
                {t('unitDetails.sleeping.extraBeds.price')}
              </label>
              <div className="relative max-w-48">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={value.extraBeds.price || ''}
                  onChange={(e) => updateExtraBeds({ price: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="w-full px-3 py-2 pr-8 rounded-lg border bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">{getCurrencySymbol(currency)}</span>
              </div>
            </div>

            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">
                {t('unitDetails.sleeping.extraBeds.generalNotes', 'Opća napomena (vrijedi za sve dodatne krevete)')}
              </label>
              <textarea
                value={value.extraBeds.notes || ''}
                onChange={(e) => updateExtraBeds({ notes: e.target.value })}
                placeholder={t('unitDetails.sleeping.extraBeds.notesPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BedConfigurator;
